import { useParams } from "react-router-dom";
import { getApiUrl } from "../utils/api";
import { useEffect, useState, useRef, useCallback } from "react";
import { convertFromRaw } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { useNavigate } from "react-router-dom";
import StartChat from "../components/StartChat";
import Skeleton, { SkeletonText, SkeletonAvatar } from "../components/Skeleton";
import { FaThumbtack, FaEllipsisV } from "react-icons/fa";
import BadgeIcon from "../components/BadgeIcon";
import { Helmet } from "react-helmet-async";
import EmptyState from "../components/EmptyState";
import PageWithRail from "../components/PageWithRail";
import { useContext } from "react";
import { UserContext } from "../context/UserContext";
import Modal from "../components/Modal";
import { useToast } from "../context/ToastContext";
import { getAvatarUrl, DEFAULT_AVATAR_DATA_URL, FALLBACK_AVATAR_SVG } from "../utils/avatar";
import { getProfileCoverUrl, onProfileCoverError } from "../utils/cover";
import { usePaginatedTab } from "../hooks/usePaginatedTab";
import ProfileTabLoadMore from "../components/ProfileTabLoadMore";

function getPoemPlainText(poem, maxLen = 180) {
  try {
    let raw = poem?.content;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
        if (typeof raw === "string") raw = JSON.parse(raw);
      } catch {
        /* plain string content */
      }
    }
    let html = "";
    if (raw && raw.blocks && typeof raw.entityMap !== "undefined") {
      html = stateToHTML(convertFromRaw(raw));
    } else {
      html = poem?.content || "";
    }
    const div = document.createElement("div");
    div.innerHTML = html;
    const text = (div.textContent || div.innerText || "").trim();
    if (!text) return "";
    return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
  } catch {
    return "";
  }
}

function formatPoemDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

function UserProfile() {
  const navigate = useNavigate();
  const { user: loggedInViewer } = useContext(UserContext);
  const { showToast } = useToast();

  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [pinnedPoemId, setPinnedPoemId] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [activeTab, setActiveTab] = useState("poems");
  const [isLoading, setIsLoading] = useState(true);
  const [viewerFollowingIds, setViewerFollowingIds] = useState(new Set());
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockedByUser, setBlockedByUser] = useState(false);
  const [blockModal, setBlockModal] = useState({ open: false });
  const [blocking, setBlocking] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);
  const profileTabsRef = useRef(null);
  const loggedInUser = JSON.parse(localStorage.getItem("user"));

  const fetchUser = async () => {
    try {
      const res = await fetch(getApiUrl(`/api/user/${username}`), {
        credentials: "include",
      });

      if (res.status === 404) {
        // User not found, redirect to 404 page
        navigate("/404", { replace: true });
        return;
      }

      if (!res.ok) {
        // Other HTTP errors, redirect to 404 page
        navigate("/404", { replace: true });
        return;
      }

      const data = await res.json();

      if (!data.user) {
        // No user data in response, redirect to 404 page
        navigate("/404", { replace: true });
        return;
      }

      setProfile(data.user);
      setPinnedPoemId(data.pinnedPoemId || null);
      setIsBlocked(data.isBlocked || false);
      setBlockedByUser(data.blockedByUser || false);
      setIsFollowing(Boolean(data.isFollowing));
    } catch (error) {
      // Network or other errors, redirect to 404 page
      navigate("/404", { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [username]);

  // Handle clicks outside the more menu dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        moreMenuRef.current &&
        !moreMenuRef.current.contains(event.target)
      ) {
        setShowMoreMenu(false);
      }
    };

    if (showMoreMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showMoreMenu]);

  useEffect(() => {
    setActiveTab("poems");
  }, [username]);

  const fetchUserPoems = useCallback(
    async (page, limit) => {
      const res = await fetch(
        getApiUrl(`/api/user/${username}/poems?page=${page}&limit=${limit}`),
        { credentials: "include" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return { items: data.poems || [], pagination: data.pagination };
    },
    [username]
  );

  const fetchUserFollowers = useCallback(
    async (page, limit) => {
      const res = await fetch(
        getApiUrl(`/api/user/${username}/followers?page=${page}&limit=${limit}`),
        { credentials: "include" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return { items: data.users || [], pagination: data.pagination };
    },
    [username]
  );

  const fetchUserFollowing = useCallback(
    async (page, limit) => {
      const res = await fetch(
        getApiUrl(`/api/user/${username}/following?page=${page}&limit=${limit}`),
        { credentials: "include" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return { items: data.users || [], pagination: data.pagination };
    },
    [username]
  );

  const poemsFeed = usePaginatedTab({
    enabled: !!profile && activeTab === "poems" && !isBlocked,
    resetKey: `${username}-poems`,
    fetchPage: fetchUserPoems,
  });
  const followersFeed = usePaginatedTab({
    enabled: !!profile && activeTab === "followers" && !isBlocked,
    resetKey: `${username}-followers`,
    fetchPage: fetchUserFollowers,
  });
  const followingFeed = usePaginatedTab({
    enabled: !!profile && activeTab === "following" && !isBlocked,
    resetKey: `${username}-following`,
    fetchPage: fetchUserFollowing,
  });

  const poems = poemsFeed.items;

  // Fetch current viewer following list once (for button state)
  useEffect(() => {
    const loadViewerFollowing = async () => {
      try {
        const res = await fetch(getApiUrl("/api/user/profile"), {
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json();
        const ids = new Set(
          Array.isArray(data.user?.following)
            ? data.user.following.map((u) => String(u._id || u))
            : []
        );
        setViewerFollowingIds(ids);
      } catch {}
    };
    if (loggedInUser?._id) loadViewerFollowing();
  }, [loggedInUser?._id]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 200);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const activeTabEl = profileTabsRef.current?.querySelector(
      ".peer-home-tab--active"
    );
    activeTabEl?.scrollIntoView({
      inline: "nearest",
      block: "nearest",
      behavior: "smooth",
    });
  }, [activeTab]);

  // Listen for userUnblocked event to refresh profile if needed
  useEffect(() => {
    const handleUserUnblocked = (event) => {
      if (profile && event.detail?.userId === profile._id) {
        fetchUser();
      }
    };
    window.addEventListener("userUnblocked", handleUserUnblocked);
    return () => window.removeEventListener("userUnblocked", handleUserUnblocked);
  }, [profile]);

  const followUser = async (uname, userId) => {
    try {
      const res = await fetch(getApiUrl(`/api/user/${uname}/follow`), {
        method: "PUT",
        credentials: "include",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      if (res.ok) {
        // Update Set with user ID, not username
        if (userId) {
          setViewerFollowingIds((prev) => new Set(prev).add(String(userId)));
        }
        // Refresh viewed profile to reflect counts if needed
        fetchUser();
      }
    } catch {}
  };

  const unfollowUser = async (uname, userId) => {
    try {
      const res = await fetch(
        getApiUrl(`/api/user/${uname}/unfollow`),
        {
          method: "PUT",
          credentials: "include",
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        }
      );
      if (res.ok) {
        // Update Set with user ID, not username
        if (userId) {
          setViewerFollowingIds((prev) => {
            const next = new Set(prev);
            next.delete(String(userId));
            return next;
          });
        }
        fetchUser();
      }
    } catch {}
  };

  const handleFollow = async () => {
    const res = await fetch(
      getApiUrl(`/api/user/${profile.username}/follow`),
      {
        method: "PUT",
        credentials: "include", // Include cookies for authentication
      }
    );
    if (res.ok) {
      setIsFollowing(true);
      fetchUser(); // refresh to update counts
    }
  };

  const handleUnfollow = async () => {
    const res = await fetch(
      getApiUrl(`/api/user/${profile.username}/unfollow`),
      {
        method: "PUT",
        credentials: "include", // Include cookies for authentication
      }
    );
    if (res.ok) {
      setIsFollowing(false);
      fetchUser(); // refresh to update counts
    }
  };

  const handleBlockUser = async () => {
    setBlocking(true);
    try {
      const res = await fetch(
        getApiUrl(`/api/user/${profile.username}/block`),
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (res.ok) {
        setIsBlocked(true);
        setIsFollowing(false); // Unfollow when blocking
        setBlockModal({ open: false });
        showToast({ type: "success", message: "User blocked successfully" });
        fetchUser(); // Refresh to update state
        // Dispatch event to refresh home feed
        window.dispatchEvent(
          new CustomEvent("userBlocked", { detail: { userId: profile._id } })
        );
      } else {
        const error = await res.json();
        showToast({ type: "error", message: error.error || "Failed to block user" });
      }
    } catch (error) {
      showToast({ type: "error", message: "Failed to block user" });
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblockUser = async () => {
    setBlocking(true);
    try {
      const res = await fetch(
        getApiUrl(`/api/user/${profile.username}/block`),
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      if (res.ok) {
        setIsBlocked(false);
        setBlockModal({ open: false });
        showToast({ type: "success", message: "User unblocked successfully" });
        fetchUser(); // Refresh to update state
        // Dispatch event to refresh home feed
        window.dispatchEvent(
          new CustomEvent("userUnblocked", { detail: { userId: profile._id } })
        );
      } else {
        const error = await res.json();
        showToast({ type: "error", message: error.error || "Failed to unblock user" });
      }
    } catch (error) {
      showToast({ type: "error", message: "Failed to unblock user" });
    } finally {
      setBlocking(false);
    }
  };

  if (!profile) {
    return (
      <PageWithRail showRail={false}>
      <div className="w-full">
        <div className="profile-hero">
          <Skeleton className="profile-hero__cover rounded-none" />
          <div className="profile-hero__body">
            <div className="profile-hero__top">
              <Skeleton className="h-[6.5rem] w-[6.5rem] sm:h-[7.5rem] sm:w-[7.5rem] rounded-full border-4 border-white" />
              <Skeleton className="h-10 w-28 rounded-lg" />
            </div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-32 mb-4" />
            <SkeletonText lines={2} className="mb-4 max-w-xl" />
            <div className="flex gap-3 mt-4 pt-4 border-t border-[#eeeeee]">
              <Skeleton className="h-14 w-24 rounded-xl" />
              <Skeleton className="h-14 w-24 rounded-xl" />
              <Skeleton className="h-14 w-24 rounded-xl" />
            </div>
          </div>
        </div>
        <div className="flex gap-6 border-b mb-6 max-w-[52rem] mx-auto">
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-24" />
        </div>
        <div className="profile-panel space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="profile-poem-card pointer-events-none">
              <Skeleton className="h-3 w-16 mb-3" />
              <Skeleton className="h-5 w-1/2 mb-2" />
              <SkeletonText lines={2} />
            </div>
          ))}
        </div>
      </div>
      </PageWithRail>
    );
  }

  const getAvatar = (u) => getAvatarUrl(u?.avatar);

  const getCoverImage = (user) => getProfileCoverUrl(user?.coverImage);

  // Helper for joined date
  const joinedDate = profile.createdAt
    ? new Date(profile.createdAt).getFullYear()
    : "2018";

  return (
    <PageWithRail showRail={false}>
    <div className="w-full min-w-0">
      <Helmet>
        <title>{`${profile.username} – Profile | PeerPoetry`}</title>
        <meta
          name="description"
          content={`${profile.username}'s profile on PeerPoetry. Followers: ${profile.followerCount ?? 0}.`}
        />
        <link rel="canonical" href={`${window.location.origin}/users/${profile.username}`} />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={`${profile.username} – Profile | PeerPoetry`} />
        <meta property="og:description" content={`View poems and activity from @${profile.username}.`} />
        <meta property="og:url" content={`${window.location.origin}/users/${profile.username}`} />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      {/* Profile header */}
      <section className="profile-hero" aria-label={`${profile.username}'s profile`}>
        <div className="profile-hero__cover">
          <img
            src={getCoverImage(profile)}
            alt=""
            className="profile-hero__cover-img"
            onError={onProfileCoverError}
          />
          <div className="profile-hero__cover-shade" aria-hidden="true" />
        </div>

        <div className="profile-hero__body">
          <div className="profile-hero__top">
            <div className="profile-hero__avatar-wrap">
              <img
                src={getAvatar(profile)}
                alt=""
                className="profile-hero__avatar"
                onError={(e) => {
                  e.currentTarget.src =
                    e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                      ? FALLBACK_AVATAR_SVG
                      : DEFAULT_AVATAR_DATA_URL;
                }}
              />
            </div>

            <div className="profile-hero__actions">
              {blockedByUser ? null : loggedInUser && loggedInUser._id !== profile._id ? (
                !isBlocked ? (
                  <>
                    <button
                      type="button"
                      onClick={isFollowing ? handleUnfollow : handleFollow}
                      className={
                        isFollowing
                          ? "profile-hero__btn-secondary"
                          : "peer-home-btn-primary"
                      }
                    >
                      {isFollowing ? "Unfollow" : "Follow"}
                    </button>
                    <StartChat
                      userId={profile._id}
                      username={profile.username}
                      name={profile.name}
                      avatar={profile.avatar}
                    />
                    <div className="relative" ref={moreMenuRef}>
                      <button
                        type="button"
                        onClick={() => setShowMoreMenu(!showMoreMenu)}
                        className="profile-hero__btn-secondary profile-hero__btn-icon"
                        aria-label="More options"
                      >
                        <FaEllipsisV className="w-4 h-4" />
                      </button>
                      {showMoreMenu && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50 py-1">
                          <button
                            type="button"
                            onClick={() => {
                              setShowMoreMenu(false);
                              setBlockModal({ open: true });
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                          >
                            Block user
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handleUnblockUser}
                    disabled={blocking}
                    className="profile-hero__btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {blocking ? "Unblocking..." : "Unblock"}
                  </button>
                )
              ) : null}
            </div>
          </div>

          <div className="profile-hero__identity">
            {blockedByUser ? (
              <p className="profile-empty-note" style={{ padding: "0.5rem 0", textAlign: "left" }}>
                This user is not available
              </p>
            ) : (
              <>
                {profile.name ? (
                  <h1 className="profile-hero__name">{profile.name}</h1>
                ) : (
                  <h1 className="profile-hero__name">@{profile.username}</h1>
                )}
                <div className="profile-hero__handle">
                  <span>@{profile.username}</span>
                  <BadgeIcon type={profile.badge} />
                </div>
                {profile.bio && <p className="profile-hero__bio">{profile.bio}</p>}
                {joinedDate && (
                  <div className="profile-hero__meta">
                    <span>Joined {joinedDate}</span>
                  </div>
                )}
              </>
            )}
          </div>

          {!blockedByUser && (
            <div className="profile-hero__stats" role="group" aria-label="Profile stats">
              <button
                type="button"
                className={`profile-hero__stat${activeTab === "poems" ? " profile-hero__stat--active" : ""}`}
                onClick={() => setActiveTab("poems")}
              >
                <span className="profile-hero__stat-value">
                  {profile.poemCount ?? poemsFeed.pagination.totalItems ?? poems.length}
                </span>
                <span className="profile-hero__stat-label">Poems</span>
              </button>
              <button
                type="button"
                className={`profile-hero__stat${activeTab === "followers" ? " profile-hero__stat--active" : ""}`}
                onClick={() => setActiveTab("followers")}
              >
                <span className="profile-hero__stat-value">
                  {profile.followerCount ?? 0}
                </span>
                <span className="profile-hero__stat-label">Followers</span>
              </button>
              <button
                type="button"
                className={`profile-hero__stat${activeTab === "following" ? " profile-hero__stat--active" : ""}`}
                onClick={() => setActiveTab("following")}
              >
                <span className="profile-hero__stat-value">
                  {profile.followingCount ?? 0}
                </span>
                <span className="profile-hero__stat-label">Following</span>
              </button>
            </div>
          )}
        </div>
      </section>
      {/* Tabs */}
      <div className="profile-tabs-wrap">
      <div className="profile-tabs" ref={profileTabsRef} role="tablist">
        <button
          type="button"
          className={`peer-home-tab${
            activeTab === "poems" ? " peer-home-tab--active" : ""
          }`}
          onClick={() => setActiveTab("poems")}
        >
          Poems
          <span className="profile-tab-count">
            {profile.poemCount ?? poemsFeed.pagination.totalItems ?? poems.length}
          </span>
        </button>
        <button
          type="button"
          className={`peer-home-tab${
            activeTab === "followers" ? " peer-home-tab--active" : ""
          }`}
          onClick={() => setActiveTab("followers")}
        >
          Followers
          <span className="profile-tab-count">
            {profile.followerCount ?? 0}
          </span>
        </button>
        <button
          type="button"
          className={`peer-home-tab${
            activeTab === "following" ? " peer-home-tab--active" : ""
          }`}
          onClick={() => setActiveTab("following")}
        >
          Following
          <span className="profile-tab-count">
            {profile.followingCount ?? 0}
          </span>
        </button>
      </div>
      </div>
      {/* Tab content */}
      {activeTab === "poems" && (
        <div className="profile-panel">
          {isLoading || poemsFeed.loading ? (
            Array.from({ length: 3 }).map((_, idx) => (
              <div key={idx} className="profile-poem-card pointer-events-none mb-3">
                <Skeleton className="h-3 w-16 mb-3" />
                <Skeleton className="h-6 w-1/2 mb-2" />
                <SkeletonText lines={2} />
              </div>
            ))
          ) : isBlocked ? (
            <p className="profile-empty-note">This user is blocked. Unblock them to see their content.</p>
          ) : poems.length === 0 ? (
            <EmptyState
              icon="poem"
              title={`${profile?.username || "This user"} hasn't written any poems yet`}
              message={
                loggedInViewer && loggedInViewer.username === username
                  ? "Start sharing your poetry with the world! Write your first poem and let your creativity shine."
                  : "This user hasn't published any poems yet. Check back later to see their creative works!"
              }
              action={
                loggedInViewer && loggedInViewer.username === username ? "write" : undefined
              }
              actionLabel={
                loggedInViewer && loggedInViewer.username === username
                  ? "Write Your First Poem"
                  : undefined
              }
              actionLink={
                loggedInViewer && loggedInViewer.username === username ? "/write" : undefined
              }
              className="py-8"
            />
          ) : (
            <>
              {poems.map((poem, idx) => {
                const excerpt = getPoemPlainText(poem);
                const dated = formatPoemDate(poem.createdAt);
                const isPinned =
                  idx === 0 &&
                  pinnedPoemId &&
                  String(poem._id) === String(pinnedPoemId);
                return (
                  <article
                    key={poem._id}
                    className="profile-poem-card"
                    onClick={() => navigate(`/poems/${poem._id}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/poems/${poem._id}`);
                      }
                    }}
                    role="link"
                    tabIndex={0}
                  >
                    <div className="profile-poem-card__eyebrow">
                      {isPinned ? (
                        <span className="profile-poem-card__pin inline-flex items-center gap-1">
                          <FaThumbtack className="w-3 h-3" /> Pinned
                        </span>
                      ) : (
                        <span>Poem</span>
                      )}
                    </div>
                    <h4 className="profile-poem-card__title">{poem.title}</h4>
                    {excerpt && (
                      <p className="profile-poem-card__excerpt">{excerpt}</p>
                    )}
                    {dated && (
                      <div className="profile-poem-card__meta">{dated}</div>
                    )}
                  </article>
                );
              })}
              <ProfileTabLoadMore
                sentinelRef={poemsFeed.sentinelRef}
                loadingMore={poemsFeed.loadingMore}
                hasNextPage={poemsFeed.pagination.hasNextPage}
                onLoadMore={poemsFeed.loadMore}
                itemCount={poems.length}
              />
            </>
          )}
        </div>
      )}

      {activeTab === "followers" && (
        <div className="py-8">
          {isBlocked ? (
            <p className="text-gray-500 text-center">This user is blocked. Unblock them to see their followers.</p>
          ) : followersFeed.loading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 peer-home-card border border-[#eeeeee]">
                  <SkeletonAvatar size="w-10 h-10" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
          ) : followersFeed.items.length > 0 ? (
            <div className="space-y-4">
              {followersFeed.items.map((follower) => (
                <div
                  key={follower._id}
                  className="flex items-center gap-4 p-4 peer-home-card border border-[#eeeeee] hover:border-[#ffd4bc] transition-all duration-300 hover:-translate-y-0.5"
                >
                  <img
                    src={getAvatar(follower)}
                    alt="avatar"
                    className="w-10 h-10 rounded-full object-cover border"
                    onError={(e) => {
                      e.currentTarget.src =
                        e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                          ? FALLBACK_AVATAR_SVG
                          : DEFAULT_AVATAR_DATA_URL;
                    }}
                  />
                  <div className="flex-1">
                    <span
                      className="text-[#f26522] hover:underline cursor-pointer font-medium text-gray-800"
                      onClick={() => navigate(`/users/${follower.username}`)}
                    >
                      {follower.username}
                    </span>
                  </div>
                  {loggedInUser && loggedInUser.username !== follower.username && (
                    viewerFollowingIds.has(String(follower._id)) ? (
                      <button
                        onClick={() => unfollowUser(follower.username, follower._id)}
                        className="ml-auto text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                      >
                        Unfollow
                      </button>
                    ) : (
                      <button
                        onClick={() => followUser(follower.username, follower._id)}
                        className="ml-auto text-xs px-2 py-1 rounded border border-[#f26522] text-[#f26522] hover:bg-[#f26522]/10"
                      >
                        Follow
                      </button>
                    )
                  )}
                </div>
              ))}
              <ProfileTabLoadMore
                sentinelRef={followersFeed.sentinelRef}
                loadingMore={followersFeed.loadingMore}
                hasNextPage={followersFeed.pagination.hasNextPage}
                onLoadMore={followersFeed.loadMore}
                itemCount={followersFeed.items.length}
              />
            </div>
          ) : (
            <p className="text-gray-500 text-center">No followers yet.</p>
          )}
        </div>
      )}
      {activeTab === "following" && (
        <div className="py-8">
          {isBlocked ? (
            <p className="text-gray-500 text-center">This user is blocked. Unblock them to see who they're following.</p>
          ) : followingFeed.loading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 peer-home-card border border-[#eeeeee]">
                  <SkeletonAvatar size="w-10 h-10" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
          ) : followingFeed.items.length > 0 ? (
            <div className="space-y-4">
              {followingFeed.items.map((followed) => (
                <div
                  key={followed._id}
                  className="flex items-center gap-4 p-4 peer-home-card border border-[#eeeeee] hover:border-[#ffd4bc] transition-all duration-300 hover:-translate-y-0.5"
                >
                  <img
                    src={getAvatar(followed)}
                    alt="avatar"
                    className="w-10 h-10 rounded-full object-cover border"
                    onError={(e) => {
                      e.currentTarget.src =
                        e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                          ? FALLBACK_AVATAR_SVG
                          : DEFAULT_AVATAR_DATA_URL;
                    }}
                  />
                  <div className="flex-1">
                    <span
                      className="font-medium text-gray-800 hover:underline cursor-pointer"
                      onClick={() => navigate(`/users/${followed.username}`)}
                    >
                      {followed.username}
                    </span>
                  </div>
                  {loggedInUser && loggedInUser.username !== followed.username && (
                    viewerFollowingIds.has(String(followed._id)) ? (
                      <button
                        onClick={() => unfollowUser(followed.username, followed._id)}
                        className="ml-auto text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                      >
                        Unfollow
                      </button>
                    ) : (
                      <button
                        onClick={() => followUser(followed.username, followed._id)}
                        className="ml-auto text-xs px-2 py-1 rounded border border-[#f26522] text-[#f26522] hover:bg-[#f26522]/10"
                      >
                        Follow
                      </button>
                    )
                  )}
                </div>
              ))}
              <ProfileTabLoadMore
                sentinelRef={followingFeed.sentinelRef}
                loadingMore={followingFeed.loadingMore}
                hasNextPage={followingFeed.pagination.hasNextPage}
                onLoadMore={followingFeed.loadMore}
                itemCount={followingFeed.items.length}
              />
            </div>
          ) : (
            <p className="text-gray-500 text-center">
              Not following anyone yet.
            </p>
          )}
        </div>
      )}
      <Modal
        open={blockModal.open}
        title="Block User"
        message={`Are you sure you want to block @${profile?.username}? You won't be able to see their content, and they won't be able to see yours.`}
        type="error"
        onClose={() => setBlockModal({ open: false })}
        onConfirm={handleBlockUser}
        confirmText="Block"
        confirmLoading={blocking}
      />
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 peer-home-btn-primary px-4 py-3 sm:px-5 sm:py-3 rounded-full shadow-lg z-50 text-sm sm:text-base"
          aria-label="Back to Top"
        >
          ↑ Back to Top
        </button>
      )}
    </div>
    </PageWithRail>
  );
}

export default UserProfile;
